import React, { CSSProperties, useEffect, useState } from 'react';
import { Skeleton, useTheme } from '@mui/material';
import GenericProfile from '../Icons/GenericProfile';
import ImageModal from './imageModal';

export interface Sizes {
  width: number;
  height: number;
}

export function createUrl(src: string): string {
  return `${src}?${performance.now()}`;
}

export interface ProfileImageProps {
  src: string;
  imgWidth?: number | string;
  imgHeight?: number | string;
  squaredImg?: boolean;
  cornerRadius?: number;
  imageText?: string;
  noSkeletonMargin?: boolean;
  onEmpty?: React.JSX.Element;
  style?: CSSProperties;
}

const ProfileImage: React.FC<ProfileImageProps> = ({
  src,
  imgWidth,
  imgHeight = 35,
  squaredImg,
  cornerRadius,
  imageText,
  style,
  noSkeletonMargin = false,
  onEmpty = <GenericProfile size={imgHeight} cornerRadius={cornerRadius} />,
}) => {
  const [picURL, setPicURL] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [imageRealSizes, setImageRealSizes] = useState<Sizes>();

  const theme = useTheme();

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const imageUrl = createUrl(src);
    const img = new Image();

    img.src = imageUrl;
    img.onload = () => {
      if (isMounted) {
        setImageRealSizes({
          height: img.height,
          width: img.width,
        });
        setPicURL(imageUrl);
        setIsLoading(false);
      }
    };
    img.onerror = () => {
      if (isMounted) {
        setPicURL(null);
        setIsLoading(false);
      }
    };

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [src]);

  return (
    <>
      {isLoading && (
        <Skeleton
          variant="rectangular"
          width={imgWidth || imgHeight}
          height={imgHeight}
          style={{ marginRight: noSkeletonMargin ? 0 : theme.spacing(1) }}
          data-testid="profile-img-skeleton"
        />
      )}
      {!isLoading && picURL && (
        <ImageModal
          picURL={picURL}
          imageRealSizes={imageRealSizes}
          imgWidth={imgWidth}
          style={style}
          imgHeight={imgHeight}
          squaredImg={squaredImg}
          cornerRadius={cornerRadius}
          imageText={imageText}
        />
      )}
      {!isLoading && !picURL && onEmpty}
    </>
  );
};

export default ProfileImage;
